import type { FieldGroup } from '@app/types/form.types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InventoryItem } from '../../types';

export type CategoryOption = { label: string; value: number };

/**
 * Config-driven field groups for the inventory item create/edit form.
 *
 * Category options are supplied by the caller (typically fetched via
 * `listCategories()` in the hosting component) rather than fetched here, so the
 * config stays presentation-only and easy to test.
 */
export const useInventoryFormConfig = (categoryOptions: CategoryOption[] = []): FieldGroup[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        title: t('inventory-form-section-details'),
        fields: [
          {
            name: 'name',
            label: t('inventory-field-name'),
            type: 'text',
            width: 'half',
            rules: { required: t('inventory-validation-name-required') },
          },
          {
            name: 'code',
            label: t('inventory-field-code'),
            type: 'text',
            width: 'half',
          },
          {
            name: 'unit',
            label: t('inventory-field-unit'),
            type: 'text',
            width: 'half',
            rules: { required: t('inventory-validation-unit-required') },
          },
          {
            name: 'categoryId',
            label: t('inventory-field-category'),
            type: 'select',
            width: 'half',
            options: categoryOptions,
          },
          {
            name: 'description',
            label: t('inventory-field-description'),
            type: 'textarea',
            width: 'full',
          },
        ],
      },
      {
        title: t('inventory-form-section-type-pricing'),
        fields: [
          {
            name: 'isConsumable',
            label: t('inventory-field-is-consumable'),
            type: 'toggle',
            width: 'third',
            rules: {
              // Mirror the backend CHECK: an item must have at least one capability.
              validate: (_value: unknown, formValues: Partial<InventoryItem>) =>
                Boolean(formValues.isConsumable || formValues.isSellable || formValues.isRentable) ||
                t('inventory-validation-capability-required'),
            },
          },
          {
            name: 'isSellable',
            label: t('inventory-field-is-sellable'),
            type: 'toggle',
            width: 'third',
          },
          {
            name: 'isRentable',
            label: t('inventory-field-is-rentable'),
            type: 'toggle',
            width: 'third',
          },
          {
            name: 'reorderThreshold',
            label: t('inventory-field-reorder-threshold'),
            type: 'number',
            width: 'third',
          },
          {
            name: 'defaultCost',
            label: t('inventory-field-default-cost'),
            type: 'number',
            width: 'third',
          },
          {
            name: 'defaultSalePrice',
            label: t('inventory-field-default-sale-price'),
            type: 'number',
            width: 'third',
          },
          {
            name: 'defaultRentalRate',
            label: t('inventory-field-default-rental-rate'),
            type: 'number',
            width: 'third',
          },
          {
            name: 'defaultRentalRateUnit',
            label: t('inventory-field-default-rental-rate-unit'),
            type: 'select',
            width: 'third',
            options: [
              { label: t('inventory-rental-rate-unit-day'), value: 'day' },
              { label: t('inventory-rental-rate-unit-week'), value: 'week' },
              { label: t('inventory-rental-rate-unit-month'), value: 'month' },
            ],
          },
        ],
      },
    ],
    [t, categoryOptions]
  );
};
